import React from "react";
import { Trophy, User } from "lucide-react";

// Two candidate pairings — each pairing has a COACH view and a PROGRAM view.
// Same red/white/blue family, but the DOMINANT color and the grey shift by role
// so a user instantly knows which "sideline" they're on.

const pairings = [
  {
    key: "home-vs-away",
    name: "PAIRING A · HOME vs AWAY",
    tagline:
      "Both roles share Red + Navy. Coach leans warm+red (the arena). Program leans cool+navy (the front office). Different greys reinforce the swap.",
    coach: {
      label: "COACH",
      subtitle: "THE ARENA",
      bg: "#2A2E35", // gunmetal (cool slate)
      card: "#343943",
      ink: "#F5F7FA",
      inkDim: "#B4B9C2",
      primary: "#EF3B2C", // blaze red — DOMINANT
      accent: "#4E7BC7", // steel blue — muted supporting
      border: "#3E434D",
    },
    program: {
      label: "PROGRAM",
      subtitle: "THE FRONT OFFICE",
      bg: "#3A3936", // field stone (warm charcoal)
      card: "#45443F",
      ink: "#F4EFE6",
      inkDim: "#B8B2A5",
      primary: "#1E3A8A", // deep navy — DOMINANT
      accent: "#D9342B", // brick red — muted supporting
      border: "#524F49",
    },
  },
  {
    key: "arena-vs-office",
    name: "PAIRING B · TURF vs BOARDROOM",
    tagline:
      "Coach on the darker sideline grey with high-energy red. Program on the lighter graphite with deep executive navy. Bigger contrast between roles.",
    coach: {
      label: "COACH",
      subtitle: "THE SIDELINE",
      bg: "#242830", // deep gunmetal
      card: "#2E333D",
      ink: "#FFFFFF",
      inkDim: "#B4B9C2",
      primary: "#FF3B30", // electric blaze red — DOMINANT
      accent: "#5B9CFF", // sky blue — supporting
      border: "#363B45",
    },
    program: {
      label: "PROGRAM",
      subtitle: "THE BOARDROOM",
      bg: "#4A4E57", // graphite mid-grey
      card: "#575C67",
      ink: "#FFFFFF",
      inkDim: "#CDD1D9",
      primary: "#0A3D91", // executive navy — DOMINANT
      accent: "#EF3B2C", // brick red — supporting
      border: "#666B75",
    },
  },
];

function RoleCard({ role, p }) {
  const isCoach = role === "coach";
  const Icon = isCoach ? User : Trophy;
  return (
    <div
      className="p-6 flex flex-col"
      style={{ background: p.bg, color: p.ink, borderRight: `1px solid ${p.border}` }}
      data-testid={`role-${p.label.toLowerCase()}`}
    >
      {/* Role header strip */}
      <div className="flex items-center gap-3 pb-4" style={{ borderBottom: `1px solid ${p.border}` }}>
        <div
          className="w-10 h-10 flex items-center justify-center"
          style={{ background: p.primary, color: "#FFFFFF" }}
        >
          <Icon size={20} strokeWidth={2} />
        </div>
        <div>
          <div className="heading text-2xl tracking-widest" style={{ color: p.ink }}>
            FOR {p.label}
          </div>
          <div className="text-[10px] tracking-widest uppercase" style={{ color: p.inkDim }}>
            {p.subtitle}
          </div>
        </div>
      </div>

      {/* Hero */}
      <div className="mt-5">
        <div className="text-[10px] tracking-widest" style={{ color: p.accent }}>
          {isCoach ? "BUILT FOR COACHES · BY COACHES" : "BUILT FOR PROGRAMS · BY COACHES"}
        </div>
        <div className="heading text-3xl mt-2 leading-none" style={{ color: p.ink }}>
          THE MAN IN THE <span style={{ color: p.primary }}>ARENA</span>.
        </div>
        <div className="text-sm mt-3" style={{ color: p.inkDim }}>
          {isCoach
            ? "Get matched anonymously to programs that fit your CIR. Your school never knows unless you say so."
            : "Continuous, anonymous talent monitoring. Candidates never see you're searching."}
        </div>
      </div>

      {/* Swatches */}
      <div className="mt-5 flex gap-2">
        <div
          className="flex-1 py-3 text-center text-[10px] tracking-widest"
          style={{ background: p.primary, color: "#FFF" }}
        >
          PRIMARY · {p.primary}
        </div>
        <div
          className="flex-1 py-3 text-center text-[10px] tracking-widest border"
          style={{ background: p.card, color: p.ink, borderColor: p.border }}
        >
          BG · {p.bg}
        </div>
        <div
          className="flex-1 py-3 text-center text-[10px] tracking-widest"
          style={{ background: p.accent, color: "#FFF" }}
        >
          ACCENT · {p.accent}
        </div>
      </div>

      {/* Pricing sample */}
      <div className="mt-5 p-4" style={{ background: p.card, border: `1px solid ${p.border}` }}>
        <div className="text-[10px] tracking-widest" style={{ color: p.primary }}>
          {isCoach ? "STEALTH MODE" : "ALWAYS-ON MONITOR"}
        </div>
        <div className="heading text-3xl mt-1" style={{ color: p.ink }}>
          {isCoach ? "$0" : "$5"}
          <span className="text-base" style={{ color: p.inkDim }}>/mo</span>
        </div>
        <ul className="text-xs mt-3 space-y-1" style={{ color: p.inkDim }}>
          <li style={{ color: p.accent }}>
            🔒 100% anonymous —{" "}
            {isCoach ? "your current school never sees this" : "candidates never see you searching"}
          </li>
          <li>· {isCoach ? "Daily AI digest of matched openings" : "All seven agents running for your program"}</li>
          <li>· {isCoach ? "Express interest anonymously" : "AI-drafted offer contracts included"}</li>
        </ul>
      </div>

      {/* CTAs */}
      <div className="mt-5 flex gap-3">
        <button
          className="heading text-base px-5 py-2.5 tracking-widest"
          style={{ background: p.primary, color: "#FFFFFF" }}
        >
          {isCoach ? "GO STEALTH →" : "START MONITORING →"}
        </button>
        <button
          className="heading text-base px-5 py-2.5 tracking-widest border"
          style={{ background: "transparent", color: p.accent, borderColor: p.accent }}
        >
          HOW IT WORKS
        </button>
      </div>

      {/* Meta chips */}
      <div className="mt-5 flex flex-wrap gap-2">
        <span className="text-[10px] tracking-widest px-2 py-1" style={{ background: `${p.accent}22`, color: p.accent }}>
          {isCoach ? "CIR · 87 ELITE" : "12 CANDIDATES · MATCH ≥ 80"}
        </span>
        <span className="text-[10px] tracking-widest px-2 py-1" style={{ background: `${p.primary}22`, color: p.primary }}>
          {isCoach ? "NEW · 4 ROLES" : "3 NEW THIS WEEK"}
        </span>
        <span
          className="text-[10px] tracking-widest px-2 py-1 border"
          style={{ color: p.inkDim, borderColor: p.border }}
        >
          NCAA · MASSACHUSETTS
        </span>
      </div>

      <div className="mt-auto pt-5 text-[10px] tracking-widest" style={{ color: p.inkDim }}>
        {p.label} · bg {p.bg} · primary {p.primary}
      </div>
    </div>
  );
}

function Pairing({ pairing }) {
  return (
    <div className="border-b border-neutral-800" data-testid={`pairing-${pairing.key}`}>
      <div className="px-6 py-4 bg-neutral-950 border-t border-neutral-800">
        <div className="heading text-2xl tracking-widest text-white">{pairing.name}</div>
        <div className="text-xs text-neutral-400 mt-1 max-w-4xl">{pairing.tagline}</div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2">
        <RoleCard role="coach" p={pairing.coach} />
        <RoleCard role="program" p={pairing.program} />
      </div>
    </div>
  );
}

export default function ColorLab() {
  return (
    <div className="min-h-screen bg-black text-white">
      <div className="px-6 py-6 border-b border-neutral-800 flex items-center justify-between">
        <div>
          <div className="heading text-3xl tracking-widest">COLOR LAB · ROLE-SPLIT</div>
          <div className="text-xs text-neutral-400 mt-1 max-w-3xl">
            Red · White · Blue on grey — but the <b className="text-white">dominant</b> color flips by role.
            Coach = red-forward (arena energy). Program = navy-forward (front office). Same DNA, different sideline.
          </div>
        </div>
        <a
          href="/"
          className="text-xs tracking-widest text-neutral-400 hover:text-white border border-neutral-700 px-4 py-2"
        >
          ← BACK TO LANDING
        </a>
      </div>

      <div data-testid="color-lab-grid">
        {pairings.map((p) => (
          <Pairing key={p.key} pairing={p} />
        ))}
      </div>

      <div className="px-6 py-8 text-xs text-neutral-500 leading-relaxed max-w-3xl">
        <p>
          <b className="text-white">How to pick:</b> Reply with <code>Pairing A</code> or <code>Pairing B</code>{" "}
          (or mix — e.g. &quot;A&apos;s coach + B&apos;s program&quot;). The shared Landing / Auth / Who We Are pages will stay
          neutral (a blend of both), then once a user identifies as coach or program, the UI shifts to their palette.
        </p>
      </div>
    </div>
  );
}
